import React, { FC } from 'react';

import { MarketingCard } from 'components/features/components/marketing-card';
import { useContentCardsNotificationsExperiment } from 'experiments/use-content-cards-notifications';
import { IMarketingCard, IMarketingCardFragment } from 'generated/sanity-graphql';
import useEffectOnce from 'hooks/use-effect-once';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { ContentCardTypes } from 'state/braze/types';
import { useExperiments } from 'state/experiments';
import { selectors, useAppSelector } from 'state/global-state';

import { BrazeMarketingCard } from '../braze-marketing-card';

import { Container } from './styled';
import { IMarketingCardGroupProps } from './types';
import { hasRules, tileShouldBeDisplayed } from './utils';

/**
 *
 * MarketingCardGroup renders a group of marketing cards in a grid
 *
 */
export const MarketingCardGroup: FC<React.PropsWithChildren<IMarketingCardGroupProps>> = ({
  doc,
  onShowTermsModal,
}) => {
  const { logExposure } = useExperiments();
  const isMobile = useIsMobileBreakpoint();
  const {
    isHomePageFeedEnabled,
    isNewsFeedEnabled,
    experimentToExpose,
  } = useContentCardsNotificationsExperiment();
  const brazeContentCards = useAppSelector(selectors.loyalty.selectHomePageFeedContentCards);

  const cards = doc.Cards as IMarketingCard[];

  useEffectOnce(() => {
    if (experimentToExpose && !isNewsFeedEnabled) {
      logExposure(experimentToExpose);
    }
  });

  if (!cards || cards.length <= 0) {
    return null;
  }

  // NOTE: Return all cards, except those cards that have rule and do not meet the requirement
  const cardsList =
    !isHomePageFeedEnabled && !isNewsFeedEnabled
      ? cards.filter(card => {
          if (hasRules(card) && !tileShouldBeDisplayed(card)) {
            return false;
          }
          return true;
        })
      : [];

  const brazeContentCardsList = isHomePageFeedEnabled ? brazeContentCards : [];

  return (
    <Container testID="marketing-tile-group-grid" flexDir={isMobile ? 'column' : 'row'}>
      {brazeContentCardsList?.map((card, index) => {
        // Only Captioned Cards are allowed
        if (card.type !== ContentCardTypes.CAPTIONED) {
          return null;
        }
        const key = `BrazeMarketingCard_${card.id}`;
        return (
          <BrazeMarketingCard
            key={key}
            card={card}
            onShowTermsModal={onShowTermsModal}
            index={index}
          />
        );
      })}

      {cardsList?.map((card, index) => {
        if (!card) {
          return null;
        }
        const key = `MarketingCard_${card._id}`;
        return (
          <MarketingCard
            key={key}
            card={card as IMarketingCardFragment}
            onShowTermsModal={onShowTermsModal}
            index={index}
          />
        );
      })}
    </Container>
  );
};
